import * as _ from 'lodash';
import { IZCValidationResult, IZCValidationTableResult } from 'src/app/interfaces/zc-validation-result.interface';
import { AttributeType, TableType } from 'src/shared/enums';
import { CalibrationSetCollectionName } from 'src/shared/enums/calibration-set-type';
import IBaseImportValidationEntity = ZoneIQCalibration.IBaseImportValidationEntity;
import IZoningCodesImportValidationResult = ZoneIQCalibration.IZoningCodesImportValidationResult;

export class ValidationService {
    static getZCValidationMessages(zcValidationResult: IZCValidationResult): IZCValidationTableResult[] {
        let result = [];

        const valuesValidationresults = [];
        [
            CalibrationSetCollectionName.CALIBRATION_ZONE_COMBINATION,
            CalibrationSetCollectionName.CALIBRATION_OVERLAY,
            CalibrationSetCollectionName.CALIBRATION_TYPOLOGY
        ].forEach(calibrationSet => {
            if (zcValidationResult[calibrationSet].isValid) {
                return;
            }
            const groupName = this.getGroupName(calibrationSet);
            [
                TableType.CALIBRATION_GENERAL,
                TableType.CALIBRATION_USES,
                TableType.CALIBRATION_PARKING,
                TableType.CALIBRATION_USE_CAPACITY,
                TableType.CALIBRATION_BONUS
            ].forEach(tableType => {
                const subGroupName = this.getSubGroupName(tableType);
                const tableTypeName = this.getTableTypeName(tableType);
                if (zcValidationResult[calibrationSet][tableTypeName].isValid) {
                    return;
                }
                _.forIn(zcValidationResult[calibrationSet][tableTypeName].validations, clbValidations => {
                    if (clbValidations.isValid) {
                        return;
                    }
                    _.forIn(clbValidations.validations, fieldValidationData => {
                        if (_.isEmpty(fieldValidationData.isValid)) {
                            return;
                        }
                        fieldValidationData.isValid.forEach(validationMessage =>
                            valuesValidationresults.push({
                                group: groupName,
                                subGroup: subGroupName,
                                category: clbValidations.calibrationSetName,
                                field: fieldValidationData.label,
                                error: validationMessage
                            })
                        );
                    });
                });
            });
        });

        result = result
            .concat(zcValidationResult.calibrationGeneral)
            .concat(zcValidationResult.calibrationUseCapacity)
            .concat(zcValidationResult.zoneCombinations)
            .concat(zcValidationResult.overlayEntities)
            .concat(zcValidationResult.calibrationUsesLabels)
            .concat(zcValidationResult.calibrationUsesTypes)
            .concat(zcValidationResult.allowedUses)
            .concat(zcValidationResult.useClasses)
            .concat(zcValidationResult.gridicsUseTags)
            .concat(valuesValidationresults)
            .concat(zcValidationResult.zoneCombinationCalibrationGeneral)
            .concat(zcValidationResult.zoneCombinationCalibrationUseCapacity)
            .concat(zcValidationResult.unknownEntities);

        return result;
    }

    static getTableTypeName(tableType: TableType): string {
        switch (tableType) {
            case TableType.CALIBRATION_GENERAL:
                return 'generalTable';
            case TableType.CALIBRATION_USES:
                return 'usesTable';
            case TableType.CALIBRATION_PARKING:
                return 'parkingTable';
            case TableType.CALIBRATION_USE_CAPACITY:
                return 'useCapacityTable';
            case TableType.CALIBRATION_BONUS:
                return 'bonusTable';
            default:
                return '';
        }
    }

    static getGroupName(csCollectionName: CalibrationSetCollectionName): string {
        switch (csCollectionName) {
            case CalibrationSetCollectionName.CALIBRATION_ZONE_COMBINATION:
                return 'Zones';
            case CalibrationSetCollectionName.CALIBRATION_OVERLAY:
                return 'Overlays';
            case CalibrationSetCollectionName.CALIBRATION_TYPOLOGY:
                return 'Typologies';
            default:
                return '';
        }
    }

    static getSubGroupName(tableType: TableType): string {
        switch (tableType) {
            case TableType.CALIBRATION_GENERAL:
                return 'General';
            case TableType.CALIBRATION_USES:
                return 'Uses';
            case TableType.CALIBRATION_PARKING:
                return 'Parking';
            case TableType.CALIBRATION_USE_CAPACITY:
                return 'Use Capacity';
            case TableType.CALIBRATION_BONUS:
                return 'Bonus';
            default:
                return '';
        }
    }

    static getSimpleEntitiesValidations(
        entities: IBaseImportValidationEntity[],
        validationField: string,
        nameField: string,
        group: string,
        subGroup: string,
        error: string,
        field?: string
    ): IZCValidationTableResult[] {
        return entities
            .filter(
                entity =>
                    entity.importValidationResult &&
                    !_.isNil(entity.importValidationResult[validationField]) &&
                    !entity.importValidationResult[validationField]
            )
            .map(entity => {
                return {
                    group,
                    subGroup,
                    category: entity[nameField],
                    field: field || '',
                    error
                };
            });
    }

    static getImportZoningCodesValidationMessages(
        validationResult: IZoningCodesImportValidationResult
    ): IZCValidationTableResult[] {
        let result = [];

        if (!validationResult) {
            return result;
        }

        const rows = validationResult.zoningCodesRows || [];

        rows.forEach(row => {
            // Zoning code entity validations
            if (row && row.zoningCode) {
                result = result
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.zoningCode],
                            'isUnique',
                            'Name',
                            'Zoning Code',
                            'Name',
                            'Zoning Code Name mentioned multiple times',
                            'Name'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.zoningCode],
                            'isMunicipalityMatch',
                            'Name',
                            'Zoning Code',
                            '',
                            'Zoning Code Municipality does not match the Municipality of existing Zoning Code',
                            'Municipality Name'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.zoningCode],
                            'isGridicsIdMatch',
                            'Name',
                            'Zoning Code',
                            '',
                            'Zoning Code Gridics Id does not match the Gridics Id of existing Zoning Code',
                            'Gridics Id'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.zoningCode],
                            'isAdditionalPlaceIdsMatch',
                            'Name',
                            'Zoning Code',
                            '',
                            'Zoning Code Additional Place ID(s) does not match the Additional Place ID(s) of existing Zoning Code',
                            'Additional Place Ids'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.zoningCode],
                            'isZoningRegulationNameMatch',
                            'Name',
                            'Zoning Code',
                            '',
                            'Zoning Code Zoning Regulation Name does not match the Zoning Regulation Name of existing Zoning Code',
                            'Zoning Regulation Name'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.zoningCode],
                            'isZoningRegulationLinkMatch',
                            'Name',
                            'Zoning Code',
                            '',
                            'Zoning Code Zoning Regulation Link does not match the Zoning Regulation Link of existing Zoning Code',
                            'Zoning Regulation Link'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.zoningCode],
                            'isDescriptionMatch',
                            'Name',
                            'Zoning Code',
                            '',
                            'Zoning Code Description does not match the Description of existing Zoning Code',
                            'Description'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.zoningCode],
                            'isPriorityMatch',
                            'Name',
                            'Zoning Code',
                            '',
                            'Zoning Code Priority does not match the Priority of existing Zoning Code',
                            'Priority'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.zoningCode],
                            'isUnitSystemMatch',
                            'Name',
                            'Zoning Code',
                            '',
                            'Zoning Code Unit System does not match the Unit System of existing Zoning Code',
                            'Unit System'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.zoningCode],
                            'isModulesMatch',
                            'Name',
                            'Zoning Code',
                            '',
                            'Zoning Code Modules does not match the Modules of existing Zoning Code',
                            'Modules'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.zoningCode],
                            'isUnitSystemSet',
                            'Name',
                            'Zoning Code',
                            '',
                            'Zoning Code Unit System is not set or invalid',
                            'Unit System'
                        )
                    );
            }

            // Municipality validations
            if (row && row.municipality) {
                result = result
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.municipality],
                            'isUnique',
                            'Name',
                            'Municipality',
                            '',
                            'Municipality name mentioned multiple times',
                            'Name'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.municipality],
                            'isGridicsInstanceDefined',
                            'Name',
                            'Municipality',
                            '',
                            'Gridics Instance is not defined for municipality',
                            'Gridics Instance'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.municipality],
                            'isDescriptionMatch',
                            'Name',
                            'Municipality',
                            '',
                            'Municipality Description does not match the Description of existing Municipality',
                            'Description'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.municipality],
                            'isGridicsIdMatch',
                            'Name',
                            'Municipality',
                            '',
                            'Municipality Gridics Jurisdiction Id does not match the Gridics Jurisdiction Id of existing Municipality',
                            'Gridics Jurisdiction Id'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.municipality],
                            'isProjectsDataSourceCategoryMatch',
                            'Name',
                            'Municipality',
                            '',
                            'Municipality ZoneIQ Projects Data Source does not match the ZoneIQ Projects Data Source of existing Municipality',
                            'Parcel Layer'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.municipality],
                            'isIndexStateLayerPRsMatch',
                            'Name',
                            'Municipality',
                            '',
                            'Municipality Index Parcels from State Layer option does not match the Index Parcels from State Layer of existing Municipality',
                            'Index State Layer'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.municipality],
                            'isLatitudeMatch',
                            'Name',
                            'Municipality',
                            '',
                            'Municipality Latitude does not match the Latitude of existing Municipality',
                            'Latitude'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.municipality],
                            'isLongitudeMatch',
                            'Name',
                            'Municipality',
                            '',
                            'Municipality Longitude does not match the Longitude of existing Municipality',
                            'Longitude'
                        )
                    )
                    .concat(
                        this.getSimpleEntitiesValidations(
                            [row.municipality],
                            'isProjectsDataSourceCategorySet',
                            'Name',
                            'Municipality',
                            '',
                            'Municipality Parcel Layer is not set or invalid',
                            'Parcel Layer'
                        )
                    );
            }

            // Gridics instance validations
            if (row && row.gridicsInstance) {
                result = result.concat(
                    this.getSimpleEntitiesValidations(
                        [row.gridicsInstance],
                        'isValid',
                        'Name',
                        'Gridics Instance',
                        '',
                        'Gridics Instance is not registered',
                        'Gridics Instance'
                    )
                );
            }

            // Modules validations (array)
            if (row && row.modules && row.modules.length) {
                result = result.concat(
                    this.getSimpleEntitiesValidations(
                        row.modules,
                        'isValid',
                        'Name',
                        'Module',
                        '',
                        'Module is not registered',
                        'Modules'
                    )
                );
            }
        });

        return result;
    }

    static getImportDescriptionsValidationMessages(
        calibrationSets: any[],
        entityTypeStr: string,
        nameField: string = 'Name'
    ): IZCValidationTableResult[] {
        let result = [];

        if (_.isEmpty(calibrationSets) || !entityTypeStr) {
            return result;
        }

        calibrationSets.forEach(row => {
            result = result
                .concat(
                    this.getSimpleEntitiesValidations(
                        [row as any],
                        'isEntityTypeAllowed',
                        nameField,
                        entityTypeStr,
                        '',
                        'Entity Type should be either Zone or Overlay',
                        `Entity Type (${row.Type})`
                    )
                )
                .concat(
                    this.getSimpleEntitiesValidations(
                        [row as any],
                        'isEntityNameSet',
                        nameField,
                        entityTypeStr,
                        '',
                        'Entity Name is required',
                        'Entity Name'
                    )
                )
                .concat(
                    this.getSimpleEntitiesValidations(
                        [row as any],
                        'isEntityExists',
                        nameField,
                        entityTypeStr,
                        '',
                        'Entity does not exist in selected Zoning Code',
                        'Entity Name'
                    )
                )
                .concat(
                    this.getSimpleEntitiesValidations(
                        [row as any],
                        'isUnique',
                        nameField,
                        entityTypeStr,
                        '',
                        'Duplicate Entity Type + Entity Name found in file',
                        'Entity Name'
                    )
                )
                .concat(
                    this.getSimpleEntitiesValidations(
                        [row as any],
                        'noSpecialCharactersInEntityType',
                        nameField,
                        entityTypeStr,
                        '',
                        'Entity Type contains special characters',
                        'Entity Type'
                    )
                )
                .concat(
                    this.getSimpleEntitiesValidations(
                        [row as any],
                        'noSpecialCharactersInEntityName',
                        nameField,
                        entityTypeStr,
                        '',
                        'Entity Name contains special characters',
                        'Entity Name'
                    )
                )
                .concat(
                    this.getSimpleEntitiesValidations(
                        [row as any],
                        'noSpecialCharactersInDescription',
                        nameField,
                        entityTypeStr,
                        '',
                        'Description contains special characters',
                        'Description'
                    )
                );
        });

        return result;
    }

    static getUnknownTableFieldsValidationMessages(unknownTableFields: any[]): IZCValidationTableResult[] {
        let result = [];

        if (_.isEmpty(unknownTableFields)) {
            return result;
        }

        unknownTableFields.forEach(row => {
            result = result
                .concat(
                    this.getSimpleEntitiesValidations(
                        [row as any],
                        'isEntityExists',
                        'Field',
                        'Table Field',
                        '',
                        'Table Field is not registered',
                        'DB Field Name'
                    )
                )
                .concat(
                    this.getSimpleEntitiesValidations(
                        [row as any],
                        'noSpecialCharactersInField',
                        'Field',
                        'Table Field',
                        '',
                        'Field Name contains special characters',
                        'DB Field Name'
                    )
                );
        });

        return result;
    }

    static getCalibrationSetTableFieldsAttributesValidationMessages(entities: any[]): IZCValidationTableResult[] {
        let result = [];

        if (_.isEmpty(entities)) {
            return result;
        }

        entities.forEach(row => {
            result = result.concat(
                this.getSimpleEntitiesValidations(
                    [row as any],
                    'noSpecialCharactersInAttributeValue',
                    'AttributeValue',
                    'Attribute',
                    '',
                    'Value contains special characters',
                    row.AttributeType === AttributeType.ASSUMPTION ? 'Assumption' : 'Description'
                )
            );
        });

        return result;
    }
}
